<script setup lang="ts">
/**
 * The conversation.
 *
 * THREE RULES, and none is negotiable.
 *
 * 1. EVERY BODY IS A TEXT NODE. `{{ paragraph }}` in a template escapes; there is
 *    no `v-html` in this app (`vue/no-v-html` is an error, and
 *    `tools/ci/frontend-bans.mjs` checks that it stays one) and this component is
 *    not going to be the first to add one. These bodies are written by agents
 *    into a form the visitor's browser renders, which is the exact shape of a
 *    stored XSS if the escaping is ever traded for "rich text support".
 *
 * 2. `bodyFormat` IS IGNORED FOR RENDERING. The contract allows `text`,
 *    `markdown` and `html`, and all three render as plain text here. A widget
 *    that interpreted `html` would be interpreting a string that reached it over
 *    the network, on behalf of a visitor, in an iframe with `allow-scripts`. When
 *    rich bodies arrive they arrive as the branded `SafeHtml` the SERVER
 *    sanitiser produces (ADR-0019) — a different type, deliberately, so that
 *    turning this on is a decision with a name on it.
 *
 * 3. WHAT IS NOT HERE CANNOT LEAK. There is no internal-note branch and no
 *    `customerVisible` check, because `rlsr_message__app_runtime__visitor_scope`
 *    is a RESTRICTIVE policy on the row: a note was never in the result set the
 *    API read for this visitor. A client-side filter would quietly assert that
 *    the row COULD arrive, and the day someone adds an endpoint that forgets the
 *    GUC, that filter is what would have been trusted to save us.
 */
import { onMounted, useTemplateRef, watch } from 'vue';

import type { WidgetMessage } from '../api/contract';
import { useVisitorFormat } from '../i18n/useVisitorFormat';
import { bodyParagraphs, type PendingMessage } from '../model/transcript';
import { useText } from '../vendor/i18n';
import { OwButton } from '../vendor/ui';

/** Shared, so a read-only transcript is not handed a new array per render. */
const NO_PENDING: readonly PendingMessage[] = [];

const props = defineProps<{
  readonly messages: readonly WidgetMessage[];
  /**
   * OPTIONAL because a PAST conversation has no outbox (P2-52). Nothing can be
   * in flight to a conversation the visitor cannot write to — `widget.messages.
   * send` resolves the conversation from the session and never from the screen —
   * so the read-only screen omits both of these rather than passing an empty
   * array and a handler that would be a bug if it were ever called.
   */
  readonly pending?: readonly PendingMessage[] | undefined;
  readonly retry?: ((clientMessageId: string) => void) | undefined;
}>();

const text = useText();
// `useVisitorFormat` rather than a shared formatter: the zone was always the
// visitor's, but this one also says WHEN — see the composable's header for the
// reply from three days ago that rendered as "17:05".
const format = useVisitorFormat();
const bottom = useTemplateRef<HTMLDivElement>('bottom');

function scrollToEnd(): void {
  // `block: 'end'` with no smooth behaviour: a reply arriving while the visitor
  // is reading should put itself where it belongs, not animate the panel out
  // from under them. It also respects reduced motion by simply not being motion.
  bottom.value?.scrollIntoView({ block: 'end' });
}

onMounted(scrollToEnd);
watch(() => [props.messages, props.pending], scrollToEnd, { flush: 'post' });
</script>

<template>
  <div
    v-if="messages.length === 0 && (pending ?? NO_PENDING).length === 0"
    class="ow-transcript ow-transcript--empty"
  >
    <p class="ow-transcript__empty">{{ text('widget.conversation.empty') }}</p>
  </div>
  <div v-else class="ow-transcript">
    <!--
      `log` rather than `feed`: a support conversation is a stream of additions
      the visitor did not ask for, and `log` is the role that makes a screen
      reader announce a new one without moving focus away from the composer.
    -->
    <ol class="ow-transcript__list" role="log" aria-live="polite" aria-relevant="additions">
      <li
        v-for="message in messages"
        :key="message.id"
        :class="message.direction === 'inbound' ? 'ow-message ow-message--own' : 'ow-message'"
      >
        <div class="ow-message__meta">
          <span class="ow-message__author">
            {{
              message.direction === 'inbound'
                ? text('widget.author.you')
                : (message.authorLabel ?? text('widget.author.team'))
            }}
          </span>
          <!--
            A machine-readable instant next to a human one, in the VISITOR's
            time zone rather than the tenant's — see src/i18n/useVisitorFormat.ts.
          -->
          <time class="ow-message__time" :datetime="message.sentAt">
            {{ format.timestamp(message.sentAt) }}
          </time>
        </div>
        <div class="ow-message__body">
          <!--
            The index belongs in this key: the paragraphs of one message have no
            ids of their own, are never reordered and are never inserted into,
            because the message is immutable once stored. That is the narrow
            case where an index key is correct rather than convenient.
          -->
          <p
            v-for="(paragraph, index) in bodyParagraphs(message.body)"
            :key="`${message.id}:${String(index)}`"
          >
            {{ paragraph }}
          </p>
        </div>
      </li>

      <li
        v-for="message in pending ?? NO_PENDING"
        :key="message.clientMessageId"
        class="ow-message ow-message--own ow-message--pending"
      >
        <div class="ow-message__meta">
          <span class="ow-message__author">{{ text('widget.author.you') }}</span>
          <span class="ow-message__status">
            {{
              message.state === 'failed'
                ? text('widget.message.failed')
                : text('widget.message.pending')
            }}
          </span>
        </div>
        <div class="ow-message__body">
          <p
            v-for="(paragraph, index) in bodyParagraphs(message.body)"
            :key="`${message.clientMessageId}:${String(index)}`"
          >
            {{ paragraph }}
          </p>
        </div>
        <OwButton
          v-if="message.state === 'failed'"
          size="sm"
          variant="ghost"
          class="ow-message__retry"
          @click="retry?.(message.clientMessageId)"
        >
          {{ text('widget.error.retry') }}
        </OwButton>
      </li>
    </ol>
    <div ref="bottom" />
  </div>
</template>
